import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { useSubscription } from '../../context/SubscriptionContext';
import membershipService from '../../services/membershipService';
import './VerificationDocumentsPage.css';

const VerificationDocumentsPage = () => {
  const navigate = useNavigate();
  const { hasPermission, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Check trustedBadgeAvailable permission after subscription loads
  useEffect(() => {
    if (!subscriptionLoading && !hasPermission('trustedBadgeAvailable')) {
      setShowUpgradeModal(true);
    }
  }, [hasPermission, subscriptionLoading]);

  const [documents, setDocuments] = useState({
    businessRegistration: null,
    tinVatCertificate: null,
    ownerIdProof: null,
    businessLicense: null,
    addressProof: null
  });

  const sidebarMenuItems = [
    { id: 'dashboard', icon: 'home', label: 'Dashboard', active: false, path: '/supplier-dashboard' },
    { id: 'products', icon: 'list', label: 'Product Lists', active: false, hasSubmenu: true, path: '/seller-dashboard' },
    { id: 'rfq', icon: 'bell', label: 'RFQ Manage', active: false, hasSubmenu: true, path: '/rfq-manage' },
    { id: 'messages', icon: 'chat', label: 'Inqs / Msgs', active: false, hasSubmenu: true, path: '/messages' },
    { id: 'membership', icon: 'membership', label: 'Membership Plan', active: true, hasSubmenu: true, path: '/membership-packages' },
    { id: 'favorite', icon: 'heart', label: 'Favorite', active: false, hasSubmenu: true, path: '/favorites' },
    { id: 'profile', icon: 'user', label: 'Profile', active: false, path: '/profile' },
  ];

  const handleFileChange = (documentType, file) => {
    setDocuments(prev => ({
      ...prev,
      [documentType]: file
    }));
  };

  const handleFileRemove = (documentType) => {
    setDocuments(prev => ({
      ...prev,
      [documentType]: null
    }));
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const seller = JSON.parse(localStorage.getItem('seller') || '{}');
    
    if (!user.id || !seller.id) {
      alert('Please login as a supplier to submit verification documents.');
      return;
    }
    
    // Check if at least business registration is provided
    if (!documents.businessRegistration) {
      alert('Business Registration Certificate is required.');
      return;
    }
    
    setSubmitting(true);
    try {
      // Build FormData for file upload
      const formData = new FormData();
      formData.append('userId', user.id);
      formData.append('sellerId', seller.id);
      
      // Default to 'verified' badge type - the simplest verification
      // Fetch badges to get the ID
      const badges = await membershipService.getBadges();
      const verifiedBadge = badges.find(b => b.name === 'verified');
      if (!verifiedBadge) {
        alert('Verification badges not available. Please try again later.');
        setSubmitting(false);
        return;
      }
      
      formData.append('badgeId', verifiedBadge.id);
      formData.append('badgeType', 'verified');
      
      // Append document files
      if (documents.businessRegistration) formData.append('businessRegistration', documents.businessRegistration);
      if (documents.tinVatCertificate) formData.append('tradeVATCertificate', documents.tinVatCertificate);
      if (documents.ownerIdProof) formData.append('companyOwnerID', documents.ownerIdProof);
      if (documents.businessLicense) formData.append('businessLicense', documents.businessLicense);
      if (documents.addressProof) formData.append('companyAddress', documents.addressProof);
      
      await membershipService.submitVerificationRequest(formData);
      setSubmitSuccess(true);
      alert('Documents submitted successfully! Admin will review your request.');
    } catch (error) {
      console.error('Error submitting documents:', error);
      alert('Failed to submit: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDocuments({
      businessRegistration: null,
      tinVatCertificate: null,
      ownerIdProof: null,
      businessLicense: null,
      addressProof: null
    });
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'home':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'list':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.25 7C3.25 6.80109 3.32902 6.61032 3.46967 6.46967C3.61032 6.32902 3.80109 6.25 4 6.25H20C20.1989 6.25 20.3897 6.32902 20.5303 6.46967C20.671 6.61032 20.75 6.80109 20.75 7C20.75 7.19891 20.671 7.38968 20.5303 7.53033C20.3897 7.67098 20.1989 7.75 20 7.75H4C3.80109 7.75 3.61032 7.67098 3.46967 7.53033C3.32902 7.38968 3.25 7.19891 3.25 7ZM3.25 12C3.25 11.8011 3.32902 11.6103 3.46967 11.4697C3.61032 11.329 3.80109 11.25 4 11.25H15C15.1989 11.25 15.3897 11.329 15.5303 11.4697C15.671 11.6103 15.75 11.8011 15.75 12C15.75 12.1989 15.671 12.3897 15.5303 12.5303C15.3897 12.671 15.1989 12.75 15 12.75H4C3.80109 12.75 3.61032 12.671 3.46967 12.5303C3.32902 12.3897 3.25 12.1989 3.25 12ZM3.25 17C3.25 16.8011 3.32902 16.6103 3.46967 16.4697C3.61032 16.329 3.80109 16.25 4 16.25H9C9.19891 16.25 9.38968 16.329 9.53033 16.4697C9.67098 16.6103 9.75 16.8011 9.75 17C9.75 17.1989 9.67098 17.3897 9.53033 17.5303C9.38968 17.671 9.19891 17.75 9 17.75H4C3.80109 17.75 3.61032 17.671 3.46967 17.5303C3.32902 17.3897 3.25 17.1989 3.25 17Z" fill="#505050"/>
          </svg>
        );
      case 'bell':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.7295 21C13.5537 21.3031 13.3014 21.5547 12.9978 21.7295C12.6941 21.9044 12.3499 21.9965 11.9995 21.9965C11.6492 21.9965 11.3049 21.9044 11.0013 21.7295C10.6977 21.5547 10.4453 21.3031 10.2695 21" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'chat':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.25 7.5H17.25V4.5C17.25 4.10218 17.092 3.72064 16.8107 3.43934C16.5294 3.15804 16.1478 3 15.75 3H3.75C3.35218 3 2.97064 3.15804 2.68934 3.43934C2.40804 3.72064 2.25 4.10218 2.25 4.5V16.5C2.25044 16.6411 2.29068 16.7792 2.36608 16.8985C2.44149 17.0177 2.54901 17.1133 2.67629 17.1742C2.80358 17.2351 2.94546 17.2589 3.08564 17.2428C3.22581 17.2266 3.3586 17.1713 3.46875 17.0831L6.75 14.4375V17.25C6.75 17.6478 6.90804 18.0294 7.18934 18.3107C7.47064 18.592 7.85218 18.75 8.25 18.75H17.0241L20.5312 21.5831C20.664 21.6905 20.8293 21.7493 21 21.75C21.1989 21.75 21.3897 21.671 21.5303 21.5303C21.671 21.3897 21.75 21.1989 21.75 21V9C21.75 8.60218 21.592 8.22064 21.3107 7.93934C21.0294 7.65804 20.6478 7.5 20.25 7.5ZM6.23906 12.9169L3.75 14.9297V4.5H15.75V12.75H6.71063C6.53897 12.75 6.37252 12.8089 6.23906 12.9169ZM20.25 19.4297L17.7609 17.4169C17.6282 17.3095 17.4629 17.2507 17.2922 17.25H8.25V14.25H15.75C16.1478 14.25 16.5294 14.092 16.8107 13.8107C17.092 13.5294 17.25 13.1478 17.25 12.75V9H20.25V19.4297Z" fill="#333333"/>
          </svg>
        );
      case 'membership':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.625 18.5625H3C2.5526 18.562 2.12367 18.3841 1.80731 18.0677C1.49095 17.7513 1.313 17.3224 1.3125 16.875V4.875C1.3125 3.94463 2.06963 3.1875 3 3.1875H5.625C5.77418 3.1875 5.91726 3.24676 6.02275 3.35225C6.12824 3.45774 6.1875 3.60082 6.1875 3.75C6.1875 3.89918 6.12824 4.04226 6.02275 4.14775C5.91726 4.25324 5.77418 4.3125 5.625 4.3125H3C2.68988 4.3125 2.4375 4.56488 2.4375 4.875V16.875C2.4375 17.1851 2.68988 17.4375 3 17.4375H5.625C5.77418 17.4375 5.91726 17.4968 6.02275 17.6023C6.12824 17.7077 6.1875 17.8508 6.1875 18C6.1875 18.1492 6.12824 18.2923 6.02275 18.3977C5.91726 18.5032 5.77418 18.5625 5.625 18.5625ZM19.875 14.2875C19.7258 14.2875 19.5827 14.2282 19.4773 14.1227C19.3718 14.0173 19.3125 13.8742 19.3125 13.725V4.875C19.3125 4.56488 19.0601 4.3125 18.75 4.3125H16.125C15.9758 4.3125 15.8327 4.25324 15.7273 4.14775C15.6218 4.04226 15.5625 3.89918 15.5625 3.75C15.5625 3.60082 15.6218 3.45774 15.7273 3.35225C15.8327 3.24676 15.9758 3.1875 16.125 3.1875H18.75C19.6804 3.1875 20.4375 3.94463 20.4375 4.875V13.725C20.4375 13.8742 20.3782 14.0173 20.2727 14.1227C20.1673 14.2282 20.0242 14.2875 19.875 14.2875Z" fill="#333333"/>
            <path d="M11.25 20.4375H6.75C6.3026 20.437 5.87367 20.2591 5.55731 19.9427C5.24095 19.6263 5.063 19.1974 5.0625 18.75V3C5.0625 2.06963 5.81963 1.3125 6.75 1.3125H15C15.9304 1.3125 16.6875 2.06963 16.6875 3V13.3837C16.6875 13.5329 16.6282 13.676 16.5227 13.7815C16.4173 13.887 16.2742 13.9462 16.125 13.9462C15.9758 13.9462 15.8327 13.887 15.7273 13.7815C15.6218 13.676 15.5625 13.5329 15.5625 13.3837V3C15.5625 2.68988 15.3101 2.4375 15 2.4375H6.75C6.43988 2.4375 6.1875 2.68988 6.1875 3V18.75C6.1875 19.0601 6.43988 19.3125 6.75 19.3125H11.25C11.3992 19.3125 11.5423 19.3718 11.6477 19.4773C11.7532 19.5827 11.8125 19.7258 11.8125 19.875C11.8125 20.0242 11.7532 20.1673 11.6477 20.2727C11.5423 20.3782 11.3992 20.4375 11.25 20.4375Z" fill="#333333"/>
            <path d="M13.5 8.81288H8.25C8.10082 8.81288 7.95774 8.75361 7.85225 8.64812C7.74676 8.54263 7.6875 8.39956 7.6875 8.25038V4.50038C7.6875 4.39591 7.71659 4.29351 7.77151 4.20465C7.82643 4.11579 7.90501 4.04398 7.99844 3.99726C8.09188 3.95054 8.19647 3.93077 8.30052 3.94015C8.40456 3.94953 8.50393 3.9877 8.5875 4.05038L9.69712 4.88288L10.4767 4.10288C10.529 4.05058 10.591 4.00908 10.6592 3.98076C10.7275 3.95244 10.8006 3.93784 10.8745 3.93781C10.9484 3.93777 11.0216 3.9523 11.0899 3.98056C11.1582 4.00882 11.2202 4.05025 11.2725 4.1025L12.0525 4.8825L13.1625 4.05C13.2461 3.98732 13.3454 3.94915 13.4495 3.93977C13.5535 3.93039 13.6581 3.95017 13.7516 3.99688C13.845 4.0436 13.9236 4.11542 13.9785 4.20428C14.0334 4.29314 14.0625 4.39554 14.0625 4.5V8.25038C14.0625 8.39956 14.0032 8.54263 13.8977 8.64812C13.7923 8.75361 13.6492 8.81288 13.5 8.81288ZM8.8125 7.68788H12.9375V5.62538L12.3375 6.07538C12.2292 6.15652 12.0952 6.19588 11.9602 6.18623C11.8252 6.17659 11.6982 6.11859 11.6025 6.02287L10.875 5.29613L10.1479 6.02363C10.0522 6.11934 9.92517 6.17734 9.79017 6.18698C9.65516 6.19663 9.52121 6.15727 9.41287 6.07613L8.8125 5.62538V7.68788ZM10.875 17.0021C10.2926 17.0021 9.762 16.6594 9.55463 16.1486C9.52537 16.0799 9.51008 16.0061 9.50966 15.9314C9.50923 15.8568 9.52367 15.7828 9.55213 15.7138C9.58059 15.6447 9.62251 15.5821 9.67544 15.5294C9.72838 15.4768 9.79126 15.4352 9.86043 15.4071C9.9296 15.379 10.0037 15.3649 10.0783 15.3658C10.153 15.3666 10.2267 15.3822 10.2953 15.4119C10.3638 15.4415 10.4258 15.4844 10.4775 15.5382C10.5293 15.592 10.5698 15.6556 10.5967 15.7253C10.6327 15.8134 10.7497 15.8771 10.875 15.8771C11.1037 15.8771 11.2969 15.7294 11.2969 15.5546V15.5108C11.2969 15.3319 10.9117 15.2036 10.779 15.1796C10.2424 15.0904 9.32812 14.6854 9.32812 13.7396V13.6965C9.32812 12.8981 10.0219 12.2486 10.875 12.2486C11.4574 12.2486 11.988 12.5914 12.1954 13.1021C12.2246 13.1708 12.2399 13.2446 12.2403 13.3193C12.2408 13.394 12.2263 13.468 12.1979 13.537C12.1694 13.606 12.1275 13.6687 12.0746 13.7213C12.0216 13.774 11.9587 13.8156 11.8896 13.8437C11.8204 13.8718 11.7463 13.8858 11.6717 13.885C11.597 13.8842 11.5233 13.8685 11.4547 13.8389C11.3862 13.8093 11.3242 13.7663 11.2725 13.7125C11.2207 13.6587 11.1802 13.5951 11.1533 13.5255C11.1173 13.4374 11.0003 13.3736 10.875 13.3736C10.6463 13.3736 10.4531 13.5214 10.4531 13.6961V13.74C10.4531 13.9189 10.8383 14.0471 10.971 14.0711C11.5076 14.1604 12.4219 14.5654 12.4219 15.5111V15.5543C12.4219 16.3526 11.7281 17.0021 10.875 17.0021Z" fill="#333333"/>
            <path d="M10.875 13.3732C10.7258 13.3732 10.5827 13.314 10.4773 13.2085C10.3718 13.103 10.3125 12.9599 10.3125 12.8107V12C10.3125 11.8508 10.3718 11.7077 10.4773 11.6023C10.5827 11.4968 10.7258 11.4375 10.875 11.4375C11.0242 11.4375 11.1673 11.4968 11.2727 11.6023C11.3782 11.7077 11.4375 11.8508 11.4375 12V12.8107C11.4375 12.9599 11.3782 13.103 11.2727 13.2085C11.1673 13.314 11.0242 13.3732 10.875 13.3732ZM10.875 17.8125C10.7258 17.8125 10.5827 17.7532 10.4773 17.6477C10.3718 17.5423 10.3125 17.3992 10.3125 17.25V16.4393C10.3125 16.2901 10.3718 16.147 10.4773 16.0415C10.5827 15.936 10.7258 15.8768 10.875 15.8768C11.0242 15.8768 11.1673 15.936 11.2727 16.0415C11.3782 16.147 11.4375 16.2901 11.4375 16.4393V17.25C11.4375 17.3992 11.3782 17.5423 11.2727 17.6477C11.1673 17.7532 11.0242 17.8125 10.875 17.8125ZM17.625 22.6875C14.8335 22.6875 12.5625 20.4165 12.5625 17.625C12.5625 14.8335 14.8335 12.5625 17.625 12.5625C20.4165 12.5625 22.6875 14.8335 22.6875 17.625C22.6875 20.4165 20.4165 22.6875 17.625 22.6875ZM17.625 13.6875C15.4538 13.6875 13.6875 15.4538 13.6875 17.625C13.6875 19.7963 15.4538 21.5625 17.625 21.5625C19.7963 21.5625 21.5625 19.7963 21.5625 17.625C21.5625 15.4538 19.7963 13.6875 17.625 13.6875Z" fill="#333333"/>
            <path d="M16.9681 19.687C16.819 19.687 16.676 19.6278 16.5706 19.5224L14.8831 17.8349C14.8285 17.7832 14.7847 17.7212 14.7545 17.6523C14.7243 17.5835 14.7082 17.5093 14.7071 17.4341C14.706 17.3589 14.72 17.2843 14.7483 17.2146C14.7766 17.145 14.8185 17.0817 14.8717 17.0285C14.9248 16.9753 14.9881 16.9333 15.0578 16.905C15.1274 16.8767 15.202 16.8627 15.2772 16.8637C15.3524 16.8647 15.4266 16.8808 15.4955 16.911C15.5643 16.9412 15.6264 16.9849 15.6781 17.0395L16.9681 18.3292L19.5706 15.7267C19.6223 15.672 19.6844 15.6283 19.7532 15.5981C19.8221 15.5679 19.8963 15.5519 19.9715 15.5508C20.0466 15.5498 20.1213 15.5639 20.1909 15.5922C20.2606 15.6205 20.3238 15.6625 20.377 15.7156C20.4301 15.7688 20.4721 15.8321 20.5004 15.9018C20.5286 15.9714 20.5427 16.0461 20.5416 16.1212C20.5405 16.1964 20.5244 16.2706 20.4942 16.3395C20.464 16.4083 20.4202 16.4704 20.3656 16.522L17.3656 19.5224C17.2602 19.6279 17.1172 19.6869 16.9681 19.687Z" fill="#333333"/>
          </svg>
        );
      case 'heart':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.8401 4.60987C20.3294 4.09888 19.7229 3.69352 19.0555 3.41696C18.388 3.14039 17.6726 2.99805 16.9501 2.99805C16.2276 2.99805 15.5122 3.14039 14.8448 3.41696C14.1773 3.69352 13.5709 4.09888 13.0601 4.60987L12.0001 5.66987L10.9401 4.60987C9.90843 3.57818 8.50915 2.99858 7.05012 2.99858C5.59109 2.99858 4.19181 3.57818 3.16012 4.60987C2.12843 5.64156 1.54883 7.04084 1.54883 8.49987C1.54883 9.95891 2.12843 11.3582 3.16012 12.3899L4.22012 13.4499L12.0001 21.2299L19.7801 13.4499L20.8401 12.3899C21.3511 11.8791 21.7565 11.2727 22.033 10.6052C22.3096 9.93777 22.4519 9.22236 22.4519 8.49987C22.4519 7.77738 22.3096 7.06198 22.033 6.39452C21.7565 5.72706 21.3511 5.12063 20.8401 4.60987Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'user':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.12305C13.5054 2.12312 14.9696 2.61781 16.167 3.53027C17.3642 4.44278 18.2286 5.72337 18.6279 7.1748C19.0272 8.62633 18.9391 10.1689 18.377 11.5654C17.8475 12.8806 16.9254 13.9977 15.7393 14.7666C18.229 15.6461 20.3582 17.3973 21.7549 19.8105L21.7969 19.8867C21.8348 19.9652 21.8608 20.0492 21.874 20.1357C21.8916 20.2511 21.8861 20.3693 21.8574 20.4824C21.8287 20.5957 21.7775 20.7027 21.707 20.7959C21.6365 20.8891 21.5474 20.967 21.4463 21.0254C21.3453 21.0837 21.2338 21.1212 21.1182 21.1357C21.0022 21.1503 20.8839 21.1421 20.7715 21.1104C20.659 21.0786 20.5542 21.0241 20.4629 20.9512C20.3717 20.8783 20.296 20.7881 20.2402 20.6855V20.6846C18.4955 17.6719 15.4151 15.8761 12 15.876C8.58344 15.876 5.50223 17.6737 3.75781 20.6885L3.75684 20.6875C3.70122 20.7891 3.62662 20.8789 3.53613 20.9512C3.44486 21.0241 3.33998 21.0787 3.22754 21.1104C3.11522 21.142 2.99762 21.1502 2.88184 21.1357C2.76594 21.1212 2.65388 21.0838 2.55273 21.0254C2.45156 20.9669 2.36249 20.8891 2.29199 20.7959C2.22151 20.7027 2.1713 20.5957 2.14258 20.4824C2.11391 20.3693 2.10746 20.2512 2.125 20.1357C2.1382 20.0492 2.1642 19.9652 2.20215 19.8867L2.24414 19.8105C3.64075 17.3965 5.77006 15.6453 8.25977 14.7666C7.07373 13.9977 6.1515 12.8806 5.62207 11.5654C5.05994 10.1689 4.97184 8.62631 5.37109 7.1748C5.77047 5.72319 6.63552 4.4428 7.83301 3.53027C9.0305 2.61774 10.4944 2.12305 12 2.12305ZM12.999 3.97363C12.005 3.77602 10.9744 3.87779 10.0381 4.26562C9.10171 4.65354 8.30138 5.31059 7.73828 6.15332C7.17516 6.99611 6.875 7.98737 6.875 9.00098C6.87659 10.3596 7.41632 11.6623 8.37695 12.623C9.33779 13.5839 10.6412 14.1245 12 14.126C13.0135 14.1259 14.004 13.8247 14.8467 13.2617C15.6894 12.6986 16.3465 11.8983 16.7344 10.9619C17.1222 10.0255 17.2241 8.99505 17.0264 8.00098C16.8287 7.00699 16.3406 6.09364 15.624 5.37695C14.9073 4.66021 13.9932 4.17138 12.999 3.97363Z" fill="#333333" stroke="#333333" strokeWidth="0.25"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const FileUploadIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.33464 10.6641V5.23073L5.6013 6.96406L4.66797 5.9974L8.0013 2.66406L11.3346 5.9974L10.4013 6.96406L8.66797 5.23073V10.6641H7.33464ZM4.0013 13.3307C3.63464 13.3307 3.32086 13.2003 3.05997 12.9394C2.79908 12.6785 2.66841 12.3645 2.66797 11.9974V9.9974H4.0013V11.9974H12.0013V9.9974H13.3346V11.9974C13.3346 12.3641 13.2042 12.6781 12.9433 12.9394C12.6824 13.2007 12.3684 13.3312 12.0013 13.3307H4.0013Z" fill="#333333"/>
    </svg>
  );

  const FileImageIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.63394 8.13828L3.25261 12.4183C3.06061 12.6583 3.25261 12.9983 3.56194 12.9983H10.3033C10.6233 12.9983 10.8046 12.6583 10.6126 12.4183L7.25261 8.13828C7.10328 7.93828 6.78328 7.93828 6.63394 8.13828Z" fill="#969696"/>
      <path d="M10.3667 10.0901L8.34008 12.6601C8.22275 12.8001 8.34008 13.0101 8.52142 13.0101H12.5641C12.7561 13.0101 12.8627 12.8101 12.7454 12.6601L10.7294 10.0901C10.7082 10.0622 10.6802 10.0395 10.6477 10.0238C10.6153 10.0082 10.5793 10 10.5427 10C10.5062 10 10.4702 10.0082 10.4378 10.0238C10.4053 10.0395 10.3773 10.0622 10.3561 10.0901H10.3667Z" fill="#969696"/>
      <path d="M10.6682 9C11.2573 9 11.7349 8.55228 11.7349 8C11.7349 7.44772 11.2573 7 10.6682 7C10.0791 7 9.60156 7.44772 9.60156 8C9.60156 8.55228 10.0791 9 10.6682 9Z" fill="#969696"/>
      <path d="M13.3331 16H2.66641C1.78107 16 1.06641 15.33 1.06641 14.5V1.5C1.06641 0.67 1.78107 0 2.66641 0H10.2291C10.6557 0 11.0611 0.16 11.3597 0.44L14.4637 3.35C14.7624 3.63 14.9331 4.01 14.9331 4.41V14.5C14.9331 15.33 14.2184 16 13.3331 16ZM2.66641 1C2.36774 1 2.13307 1.22 2.13307 1.5V14.5C2.13307 14.78 2.36774 15 2.66641 15H13.3331C13.6317 15 13.8664 14.78 13.8664 14.5V4.41C13.8673 4.34442 13.8535 4.27941 13.8259 4.21913C13.7984 4.15885 13.7576 4.10465 13.7064 4.06L10.6024 1.15C10.5039 1.05579 10.3698 1.00188 10.2291 1H2.66641Z" fill="#969696"/>
      <path d="M14.2736 5.00109H11.1696C10.3056 5.00109 9.60156 4.34109 9.60156 3.53109V0.621093C9.60156 0.341094 9.83623 0.121094 10.1349 0.121094C10.4336 0.121094 10.6682 0.341094 10.6682 0.621093V3.53109C10.6682 3.79109 10.8922 4.00109 11.1696 4.00109H14.2736C14.5722 4.00109 14.8069 4.22109 14.8069 4.50109C14.8069 4.78109 14.5722 5.00109 14.2736 5.00109Z" fill="#969696"/>
    </svg>
  );

  const DeleteIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.08301 11.25C2.76217 11.25 2.48762 11.1359 2.25934 10.9076C2.03106 10.6793 1.91673 10.4046 1.91634 10.0833V2.5H1.33301V1.33333H4.24967V0.75H7.74967V1.33333H10.6663V2.5H10.083V10.0833C10.083 10.4042 9.96889 10.6789 9.74059 10.9076C9.51229 11.1362 9.23759 11.2504 8.91634 11.25H3.08301ZM8.91634 2.5H3.08301V10.0833H8.91634V2.5ZM4.24967 8.91667H5.41634V3.66667H4.24967V8.91667ZM6.58301 8.91667H7.74967V3.66667H6.58301V8.91667Z" fill="#FF0000"/>
    </svg>
  );

  const ChevronRightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 18L15 12L9 6" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const LogoutIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.5 3.751C5.974 3.751 5.25 4.381 5.25 5.572V18.431C5.25 19.623 5.974 20.252 6.5 20.252H13.496C13.6949 20.252 13.8857 20.331 14.0263 20.4717C14.167 20.6123 14.246 20.8031 14.246 21.002C14.246 21.2009 14.167 21.3917 14.0263 21.5323C13.8857 21.673 13.6949 21.752 13.496 21.752H6.5C4.817 21.752 3.75 20.079 3.75 18.431V5.571C3.75 3.923 4.817 2.25 6.5 2.25H13.5C13.6989 2.25 13.8897 2.32902 14.0303 2.46967C14.171 2.61032 14.25 2.80109 14.25 3C14.25 3.19891 14.171 3.38968 14.0303 3.53033C13.8897 3.67098 13.6989 3.75 13.5 3.75L6.5 3.751Z" fill="#FF7474"/>
      <path d="M16.53 7.97325C16.3894 7.8328 16.1988 7.75391 16 7.75391C15.8012 7.75391 15.6106 7.8328 15.47 7.97325V11.2492H9.5C9.30109 11.2492 9.11032 11.3283 8.96967 11.4689C8.82902 11.6096 8.75 11.8003 8.75 11.9992C8.75 12.1982 8.82902 12.3889 8.96967 12.5296C9.11032 12.6702 9.30109 12.7492 9.5 12.7492H15.47V16.0332C15.6106 16.1737 15.8012 16.2526 16 16.2526C16.1988 16.2526 16.3894 16.1737 16.53 16.0332L20.03 12.5332C20.0999 12.4634 20.1553 12.3805 20.1931 12.2892C20.2308 12.1979 20.2502 12.1 20.25 12.0012V11.9992C20.2499 11.8896 20.2258 11.7812 20.1793 11.6819C20.1329 11.5825 20.0652 11.4946 19.981 11.4242L16.53 7.97325Z" fill="#FF7474"/>
    </svg>
  );

  const DocumentUploadSection = ({ title, documentType, hasFile }) => (
    <div className="vdp-document-upload-card">
      <h3 className="vdp-document-title">{title}</h3>
      <div className="vdp-document-upload-content">
        {hasFile && (
          <div className="vdp-uploaded-file-section">
            <div className="vdp-file-info-row">
              <div className="vdp-file-badge">
                <FileImageIcon />
                <span className="vdp-file-name">Choose file.PDF</span>
              </div>
              <button 
                className="vdp-delete-btn"
                onClick={() => handleFileRemove(documentType)}
                type="button"
              >
                <DeleteIcon />
              </button>
            </div>
          </div>
        )}
        <div className="vdp-file-upload-section">
          <div className="vdp-upload-input-wrapper">
            <label className="vdp-upload-input-label">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => handleFileChange(documentType, e.target.files[0])}
                className="vdp-file-input-hidden"
              />
              <div className="vdp-upload-button">
                <FileUploadIcon />
                <span>Choose file</span>
              </div>
              <span className="vdp-upload-placeholder">Upload any attachment</span>
            </label>
          </div>
          <p className="vdp-upload-hint">Upload any file type. Maximum file size: 64MB</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="verification-documents-page">
      <Header />
      
      <div className="vdp-layout">
        {/* Sidebar */}
        <aside className="vdp-sidebar">
          <div className="vdp-sidebar-profile">
            <img 
              src="/uploads/builder-images/8a9b6118da672dbfa19125b30d2ccea6449bdaf4_w520.png" 
              alt="Profile"
              className="vdp-profile-image"
            />
          </div>
          
          <nav className="vdp-sidebar-nav">
            {sidebarMenuItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`vdp-sidebar-item ${item.active ? 'active' : ''}`}
              >
                <div className="vdp-sidebar-item-content">
                  <span className="vdp-sidebar-icon">{renderIcon(item.icon)}</span>
                  <span className="vdp-sidebar-label">{item.label}</span>
                </div>
                {item.hasSubmenu && <ChevronRightIcon />}
              </Link>
            ))}
            
            <button className="vdp-sidebar-item vdp-logout-item">
              <div className="vdp-sidebar-item-content">
                <span className="vdp-sidebar-icon"><LogoutIcon /></span>
                <span className="vdp-sidebar-label vdp-logout-label">Logout</span>
              </div>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="vdp-main-content">
          <div className="vdp-content-container">
            {/* Page Header */}
            <div className="vdp-page-header">
              <div className="vdp-header-text">
                <h1 className="vdp-page-title">Membership Packages</h1>
                <p className="vdp-page-subtitle">
                  Flexible plans designed for suppliers, buyers, and professionals at every level.
                </p>
              </div>
            </div>

            {/* Verification Section */}
            <div className="vdp-verification-section">
              <div className="vdp-section-header">
                <h2 className="vdp-section-title">Verification Badges (Optional Add-ons)</h2>
                <p className="vdp-section-subtitle">
                  Boost your credibility and stand out — choose the badge level that matches your trust goals.
                </p>
              </div>

              {/* Verify Card */}
              <div className="vdp-verify-card">
                <div className="vdp-verify-header">
                  <div className="vdp-verify-info">
                    <span className="vdp-verify-label">Verify</span>
                    <span className="vdp-verify-price">$99/year</span>
                  </div>
                  <p className="vdp-verify-description">
                    Upload needed Company documents for reviewing.
                  </p>
                </div>

                {/* Documents Form */}
                <form className="vdp-documents-form" onSubmit={handleSubmit}>
                  <DocumentUploadSection 
                    title="Business Registration Certificate"
                    documentType="businessRegistration"
                    hasFile={documents.businessRegistration !== null}
                  />

                  <DocumentUploadSection 
                    title="TIN or VAT Certificate"
                    documentType="tinVatCertificate"
                    hasFile={documents.tinVatCertificate !== null}
                  />

                  <DocumentUploadSection 
                    title="Company Owner/Director ID Proof (Passport, National ID, or Driver's License)"
                    documentType="ownerIdProof"
                    hasFile={documents.ownerIdProof !== null}
                  />

                  <DocumentUploadSection 
                    title="Business License or Operating Permit"
                    documentType="businessLicense"
                    hasFile={documents.businessLicense !== null}
                  />

                  <DocumentUploadSection 
                    title="Proof of Company Address (Utility Bill or Lease Agreement)"
                    documentType="addressProof"
                    hasFile={documents.addressProof !== null}
                  />

                  {/* Action Buttons */}
                  <div className="vdp-form-actions">
                    <button 
                      type="button" 
                      className="vdp-btn-cancel"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="vdp-btn-submit"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          navigate(-1); // Go back when closing if no permission
        }}
        feature="Trusted Badge Verification"
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="Global Enterprise"
      />
    </div>
  );
};

export default VerificationDocumentsPage;
